import { z } from 'zod';
import { languages, type Language } from '../enums.js';

/**
 * Textes du courriel de rappel de rendez-vous qu'un cabinet peut écrire, par langue : l'objet,
 * l'introduction (salutation comprise) et la fin. **Rien d'autre n'est éditable** : la carte du
 * rendez-vous, le bloc « Pour annuler ou déplacer » et le pied de page sont fixés par le produit —
 * c'est ce qui garantit qu'un rappel reste un rappel, quoi que le cabinet écrive autour.
 *
 * Du texte brut, jamais du HTML : le serveur échappe tout au rendu. Un champ nul vaut « texte par
 * défaut du produit », si bien qu'un cabinet qui n'a rien écrit profite des corrections futures.
 */

/**
 * Variables insérables, en français dans les trois langues : un seul jeu de marqueurs, que les
 * pastilles de l'écran insèrent telles quelles. `{patient}` est le **prénom** seul, et aucun texte
 * par défaut ne s'en sert : nommer le patient reste un choix du cabinet.
 */
export const appointmentReminderVariables = [
  'patient',
  'date',
  'heure',
  'praticien',
  'cabinet',
  'adresse',
  'telephone',
] as const;
export type AppointmentReminderVariable = (typeof appointmentReminderVariables)[number];

export const APPOINTMENT_REMINDER_SUBJECT_MAX_LENGTH = 150;
export const APPOINTMENT_REMINDER_INTRO_MAX_LENGTH = 1000;
export const APPOINTMENT_REMINDER_INTRO_MAX_LINES = 20;
export const APPOINTMENT_REMINDER_CLOSING_MAX_LENGTH = 500;
export const APPOINTMENT_REMINDER_CLOSING_MAX_LINES = 8;

/**
 * Tout ce qui ressemble à un marqueur : des accolades autour d'un mot sans espace. Plus large que
 * le remplissage du serveur (`\w+`) exprès : `{téléphone}` doit être signalé comme inconnu, pas
 * partir tel quel chez le patient parce que l'accent l'aurait fait passer inaperçu.
 */
const MARKER = /\{([^{}\s]{1,40})\}/gu;

const KNOWN: ReadonlySet<string> = new Set(appointmentReminderVariables);

/** Marqueurs inconnus d'un texte, sans doublon, dans l'ordre d'apparition. */
export function unknownAppointmentReminderVariables(text: string): string[] {
  const unknown: string[] = [];
  for (const match of text.matchAll(MARKER)) {
    const name = match[1] ?? '';
    if (!KNOWN.has(name) && !unknown.includes(name)) unknown.push(name);
  }
  return unknown;
}

/**
 * Normalisation commune au serveur et à l'écran : retours à la ligne unifiés, espaces de fin de
 * ligne retirés, bords rognés. L'écran s'en sert pour comparer une saisie au texte par défaut.
 */
export function normalizeAppointmentReminderText(value: string): string {
  return value
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .map((line) => line.trimEnd())
    .join('\n')
    .trim();
}

/** Aucun caractère de contrôle hormis le retour à la ligne. */
function printable(value: string): boolean {
  return Array.from(value).every((char) => {
    const code = char.charCodeAt(0);
    return char === '\n' || (code >= 0x20 && code !== 0x7f);
  });
}

interface FieldRules {
  readonly max: number;
  /** Nombre de lignes au plus ; un objet de courriel n'en a qu'une. */
  readonly maxLines: number;
}

/**
 * Un champ : normalisé, borné, puis — pour l'enregistrement seulement — vérifié marqueur par
 * marqueur. La chaîne vide vaut « texte par défaut », donc `null`.
 */
function textField(rules: FieldRules, checkVariables: boolean) {
  const bounded = z
    .string()
    .max(rules.max)
    .refine((value) => value.split('\n').length <= rules.maxLines, {
      message: 'Trop de lignes',
    })
    .refine(printable, { message: 'Caractère de contrôle interdit' });
  const checked = checkVariables
    ? bounded.superRefine((value, ctx) => {
        const unknown = unknownAppointmentReminderVariables(value);
        if (unknown.length > 0) {
          ctx.addIssue({
            code: 'custom',
            message: `Variable inconnue : ${unknown.map((name) => `{${name}}`).join(', ')}`,
            params: { unknownVariables: unknown },
          });
        }
      })
    : bounded;
  return z
    .string()
    .transform(normalizeAppointmentReminderText)
    .pipe(checked)
    .transform((value) => (value === '' ? null : value))
    .nullable();
}

function textsSchema(checkVariables: boolean) {
  return z
    .object({
      subject: textField(
        { max: APPOINTMENT_REMINDER_SUBJECT_MAX_LENGTH, maxLines: 1 },
        checkVariables,
      ),
      intro: textField(
        {
          max: APPOINTMENT_REMINDER_INTRO_MAX_LENGTH,
          maxLines: APPOINTMENT_REMINDER_INTRO_MAX_LINES,
        },
        checkVariables,
      ),
      closing: textField(
        {
          max: APPOINTMENT_REMINDER_CLOSING_MAX_LENGTH,
          maxLines: APPOINTMENT_REMINDER_CLOSING_MAX_LINES,
        },
        checkVariables,
      ),
    })
    .strict();
}

/** Textes d'une langue à l'enregistrement : une variable inconnue est refusée. */
export const appointmentReminderTextsSchema = textsSchema(true);
export type AppointmentReminderTexts = z.infer<typeof appointmentReminderTextsSchema>;

/**
 * Textes d'une langue pour l'aperçu et l'essai : mêmes bornes, mais une variable inconnue passe
 * et se voit telle quelle — l'aperçu se rafraîchit pendant la frappe, et l'écran signale déjà
 * l'erreur sous le champ.
 */
export const appointmentReminderDraftTextsSchema = textsSchema(false);

/** Modèle complet : une entrée par langue, toutes facultatives (absente = textes par défaut). */
export const appointmentReminderTemplateSchema = z
  .object({
    fr: appointmentReminderTextsSchema,
    de: appointmentReminderTextsSchema,
    en: appointmentReminderTextsSchema,
  } satisfies Record<Language, typeof appointmentReminderTextsSchema>)
  .partial()
  .strict();
export type AppointmentReminderTemplate = z.infer<typeof appointmentReminderTemplateSchema>;

/** `PUT /api/organizations/current/appointment-reminders/template` : remplace le modèle entier. */
export const appointmentReminderTemplateUpdateSchema = z
  .object({ template: appointmentReminderTemplateSchema })
  .strict();
export type AppointmentReminderTemplateUpdateInput = z.infer<
  typeof appointmentReminderTemplateUpdateSchema
>;

/** Aperçu et courriel d'essai : les textes **non enregistrés** d'une langue. */
export const appointmentReminderPreviewSchema = z
  .object({
    language: z.enum(languages),
    texts: appointmentReminderDraftTextsSchema,
  })
  .strict();
export type AppointmentReminderPreviewInput = z.infer<typeof appointmentReminderPreviewSchema>;

/* -------------------------------------------------------------------------- */
/* Sorties                                                                     */
/* -------------------------------------------------------------------------- */

/** Textes par défaut d'une langue, tels que le produit les écrirait pour ce cabinet. */
export interface AppointmentReminderDefaultTexts {
  subject: string;
  intro: string;
  /** La signature du cabinet (Paramètres → Cabinet) s'il en a une, sinon la formule du produit. */
  closing: string;
}

export interface AppointmentReminderTemplateDto {
  /** Ce que le cabinet a écrit ; `null` = texte par défaut. Les trois langues, toujours. */
  template: Record<Language, AppointmentReminderTexts>;
  defaults: Record<Language, AppointmentReminderDefaultTexts>;
}

/** Rendu du vrai courriel, avec un rendez-vous d'exemple et les coordonnées du cabinet. */
export interface AppointmentReminderPreviewDto {
  subject: string;
  html: string;
  text: string;
}

export interface AppointmentReminderTestEmailDto {
  /** L'adresse de connexion de l'appelant lui-même : jamais celle d'un patient. */
  sentTo: string;
}
